import { useMemo } from "react";
import { SymbolLabel } from "../components/SymbolLabel";
import { fmtPct, fmtPx, fmtSigned, fmtUSD } from "../lib/format";
import type { Pending, Position } from "../types";

type Props = {
  pending: Pending[];
  positions: Position[];
  onOpenApproval: (approvalId: string) => void;
};

type WatchRow = {
  symbol: string;
  companyName?: string | null;
  plan: string;
  last: number | null;
  trigger: number | null;
  distancePct: number | null;
  proximity: number;
  state: "approval" | "position";
  approvalId?: string;
  riskLabel: string;
};

const proximityFromDistance = (distancePct: number | null): number => {
  if (distancePct === null) return 1;
  const abs = Math.abs(distancePct);
  if (abs <= 0.25) return 5;
  if (abs <= 0.75) return 4;
  if (abs <= 1.5) return 3;
  if (abs <= 3) return 2;
  return 1;
};

export const WatchlistScreen = ({
  pending,
  positions,
  onOpenApproval,
}: Props) => {
  const rows = useMemo<WatchRow[]>(() => {
    const approvalRows = pending.map((p): WatchRow => {
      const distancePct =
        p.entry > 0 ? ((p.entry - p.stop) / p.entry) * 100 : null;
      return {
        symbol: p.sym,
        companyName: p.companyName,
        plan: `${p.strategy} · ${fmtPx(p.entry)} stop ${fmtPx(p.stop)}`,
        last: p.entry,
        trigger: p.entry,
        distancePct,
        proximity: proximityFromDistance(distancePct),
        state: "approval",
        approvalId: p.approvalId,
        riskLabel: `${p.accountLabel} · ${fmtUSD(p.risk)}`,
      };
    });

    const positionRows = positions.map((p): WatchRow => {
      const distancePct =
        p.last > 0 ? ((p.last - p.stop) / p.last) * 100 : null;
      return {
        symbol: p.sym,
        companyName: p.companyName,
        plan: `${p.strategy ?? "open position"} · stop ${fmtPx(p.stop)}`,
        last: p.last,
        trigger: p.stop,
        distancePct,
        proximity: proximityFromDistance(distancePct),
        state: "position",
        riskLabel: `${p.qty} sh · ${fmtSigned(Math.round(p.pl))} ${fmtPct(
          p.plPct,
        )}`,
      };
    });

    return [...approvalRows, ...positionRows].sort((a, b) => {
      const ap = a.proximity;
      const bp = b.proximity;
      if (ap !== bp) return bp - ap;
      return a.symbol.localeCompare(b.symbol);
    });
  }, [pending, positions]);

  return (
    <div data-screen-label="03 Watchlist">
      <div className="tp-page-head">
        <h1>Watchlist</h1>
        <div className="meta">
          {pending.length} approval{pending.length === 1 ? "" : "s"} ·{" "}
          {positions.length} open position{positions.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="tp-tile">
        <div className="tp-tile-head">
          <span className="label">Active watches ({rows.length})</span>
          <span className="tp-muted">pending approvals + open stops</span>
        </div>
        <div className="tp-tile-body" style={{ padding: 0 }}>
          <table className="tp-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Plan</th>
                <th className="right">Last</th>
                <th className="right">Trigger</th>
                <th className="right">Distance</th>
                <th className="center">Proximity</th>
                <th className="right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="tp-empty-cell">
                    No active watch rows.
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr key={`${row.state}-${row.approvalId ?? row.symbol}-${i}`}>
                    <td className="ticker">
                      <SymbolLabel
                        symbol={row.symbol}
                        companyName={row.companyName}
                      />
                      <div className="tp-row-sub">{row.riskLabel}</div>
                    </td>
                    <td className="num">{row.plan}</td>
                    <td className="num right">
                      {row.last === null ? "—" : fmtPx(row.last)}
                    </td>
                    <td className="num right">
                      {row.trigger === null ? "—" : fmtPx(row.trigger)}
                    </td>
                    <td className="num right">
                      {row.distancePct === null
                        ? "—"
                        : `${row.distancePct >= 0 ? "+" : ""}${row.distancePct.toFixed(
                            2,
                          )}%`}
                    </td>
                    <td className="center">
                      <span className="tp-prox">
                        <span className="on">
                          {"●".repeat(row.proximity)}
                        </span>
                        <span className="off">
                          {"●".repeat(5 - row.proximity)}
                        </span>
                      </span>
                    </td>
                    <td className="right">
                      {row.approvalId ? (
                        <button
                          type="button"
                          className="tp-btn tp-btn-secondary tp-btn-sm"
                          onClick={() => onOpenApproval(row.approvalId!)}
                        >
                          Review
                        </button>
                      ) : (
                        <span className="tp-muted">open</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
